import React, { useState, useRef, useCallback, ReactNode } from 'react'
import styled from 'styled-components'
import { ExpandCircleDownOutlined } from '@mui/icons-material'

import KeyboardAccessible from 'components/KeyboardAccessible'
import Tooltip from 'components/Tooltip'

import { color } from 'styles/colors'

const ANIMATION_TIME = (window as Window & { runningSpecs?: boolean }).runningSpecs ? 0 : 400

interface CollapsiblePanelProps {
  open?: boolean
  headerComponent: ReactNode
  children?: ReactNode
  caretVerticalPosition?: string
  caretHorizontalPosition?: string
  caretSize?: string
  expandedCallback?: () => void
  collapsedCallback?: () => void
  onClickCallback?: () => void
  className?: string
}

const CollapsiblePanel = ({
  open = false,
  headerComponent,
  caretVerticalPosition = 'center',
  caretHorizontalPosition = 'left',
  caretSize = '24px',
  children = null,
  expandedCallback = () => {},
  onClickCallback = () => {},
  collapsedCallback = () => {},
  className = ''
}: CollapsiblePanelProps) => {
  const [isOpen, setIsOpen] = useState(open)
  const panelContentRef = useRef<HTMLDivElement>(null)

  const caretIconName = () => {
    if (caretHorizontalPosition === 'right') {
      return isOpen ? 'up3' : 'down3'
    } else {
      return isOpen ? 'down3' : 'right3'
    }
  }

  const onClick = useCallback(() => {
    if (panelContentRef.current && isOpen) {
      $(panelContentRef.current).slideUp(ANIMATION_TIME)
      collapsedCallback()
    } else if (panelContentRef.current && !isOpen) {
      $(panelContentRef.current).slideDown(ANIMATION_TIME)
      expandedCallback()
    }

    onClickCallback()
    setIsOpen(!isOpen)
  }, [isOpen, expandedCallback, onClickCallback, collapsedCallback])

  return (
    <S.CollapsiblePanel {...{ className }}>
      <S.Header tabIndex={0} {...{ onClick, caretHorizontalPosition }}>
        <Tooltip title={isOpen ? 'Click to minimize' : 'Click to expand'} placement="top">
          <S.CaretIcon
            {...{ caretVerticalPosition, caretSize }}
            className={`icon-arrow-${caretIconName()}`}
            isOpen={isOpen}
          />
        </Tooltip>
        <S.HeaderComponent>{headerComponent}</S.HeaderComponent>
      </S.Header>
      <S.Content {...{ isOpen }} ref={panelContentRef}>
        {children}
      </S.Content>
    </S.CollapsiblePanel>
  )
}

export default CollapsiblePanel

interface HeaderProps {
  caretHorizontalPosition?: string
  tabIndex?: number
}

interface ContentProps {
  isOpen: boolean
}

interface CaretIconProps {
  caretVerticalPosition?: string
  caretSize?: string
  isOpen: boolean
}

const S = {
  CollapsiblePanel: styled.div`
    background-color: inherit;
  `,
  Header: styled(KeyboardAccessible).withConfig({
    shouldForwardProp: (prop) => !['caretVerticalPosition', 'caretHorizontalPosition', 'tabIndex'].includes(prop)
  })<HeaderProps>`
    display: flex;
    cursor: pointer;
    flex-direction: ${({ caretHorizontalPosition }) => caretHorizontalPosition === 'left' ? 'row' : 'row-reverse'};
  `,
  HeaderComponent: styled.div`
    flex-grow: 1;
    overflow: hidden;
  `,
  Content: styled.div.withConfig({
    shouldForwardProp: (prop) => !['isOpen'].includes(prop)
  })<ContentProps>`
    ${({ isOpen }) => isOpen
    ? `display: block;
         height: auto !important;
         overflow: visible;`
    : `display: none;
         overflow: hidden;`
}
  `,
  CaretIcon: styled(ExpandCircleDownOutlined).withConfig({
    shouldForwardProp: (prop) => !['caretSize', 'caretVerticalPosition', 'isOpen'].includes(prop)
  })<CaretIconProps>`
    display: block;
    font-size: ${({ caretSize }) => caretSize};
    align-self: ${({ caretVerticalPosition }) => caretVerticalPosition};
    color: ${color('warrantyText')};
    margin: 0px 10px;
    transform: ${({ isOpen }) => !isOpen && 'rotate(-90deg);'}
  `
}
